import React, { useState, createContext, useContext } from 'react'
import { isNumber } from 'lodash'
import numbro from 'numbro'
import { capitalCase } from 'change-case'
import ListBase from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import moment from 'moment'
import ModalityStatusIndicator from './ModalityStatusIndicator'
import { makeStyles } from '@material-ui/core/styles'
import Tooltip from './Tooltip'
import Badge from './Badge'

const DANGER = '#ef5350'
const GOOD = '#2cbe4e'
const WARNING = '#ffd600'
const INDETERMINATE = '#e0e0e0'

const status = {
  green: GOOD,
  yellow: WARNING,
  red: DANGER,
  gray: INDETERMINATE
}
// Context to manage which tooltip is open (only one at a time)
const TooltipContext = createContext({
  openTooltipId: null,
  setOpenTooltipId: () => {}
})

// Provider component to wrap the list
export const HealthIndicatorProvider = ({ children }) => {
  const [openTooltipId, setOpenTooltipId] = useState(null)
  return (
    <TooltipContext.Provider value={{ openTooltipId, setOpenTooltipId }}>
      {children}
    </TooltipContext.Provider>
  )
}

// Detect touch device synchronously to avoid controlled/uncontrolled switching
const isTouchDevice = () => {
  return (
    typeof window !== 'undefined' &&
    ('ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      navigator.msMaxTouchPoints > 0)
  )
}

const useStyles = makeStyles({
  textInput: { width: 400, marginRight: 10 },
  selectInput: { width: 200 },
  field: {
    display: 'flex'
  },
  actions: {
    display: 'flex'
  },
  indicator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer'
  }
})

const getStatusText = data => {
  if (data.timeStatusColor === 'yellow' || data.timeStatusColor === 'red') {
    const time = data[data.timeStatusKeys]
    const relativeTime = moment(time, 'M/DD/YYYY h:mm a').fromNow(true)

    return `Inactive for ${relativeTime}`
  }

  return `Last report time ${data[data.timeStatusKeys]}`
}

const IndicatorItem = ({
  field = '',
  timeField = '',
  data = {},
  color,
  isTime = false
}) => {
  return (
    <ListItem style={{ justifyContent: 'flex-start' }} dense>
      <ListItemIcon>
        <Badge color={color} />
      </ListItemIcon>
      <ListItemText
        primary={capitalCase(timeField || field)}
        // primaryTypographyProps={{ style: { fontSize: 12 } }}
        style={{ marginLeft: -32 }}
      />
      <ListItemSecondaryAction style={{ color: '#444', fontWeight: '600' }}>
        {!isTime && isNumber(data[field])
          ? numbro(Number(data[field])).format({
              thousandSeparated: true,
              mantissa: 2
            })
          : data[field]}
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const HealthIndicator = data => {
  const classes = useStyles()
  const greens = data?.healthStatusMatches?.green
  const yellows = data?.healthStatusMatches?.yellow
  const reds = data?.healthStatusMatches?.red
  const touchDevice = isTouchDevice()
  const { openTooltipId, setOpenTooltipId } = useContext(TooltipContext)
  
  // Use the record ID to uniquely identify this tooltip
  // In react-admin FunctionField, the record data is passed as props directly
  const tooltipId = data?.id || null
  const tooltipOpen = touchDevice && openTooltipId === tooltipId

  const handleIndicatorClick = (e) => {
    // Stop event propagation to prevent row click
    e.stopPropagation()
    if (touchDevice && tooltipId) {
      // If clicking the same tooltip, close it. Otherwise, open this one and close others
      if (openTooltipId === tooltipId) {
        setOpenTooltipId(null)
      } else {
        setOpenTooltipId(tooltipId)
      }
    }
  }

  const handleTooltipClose = () => {
    if (touchDevice && tooltipId && openTooltipId === tooltipId) {
      setOpenTooltipId(null)
    }
  }

  const tooltipContent = (
    <div style={{ width: 250 }}>
      {getStatusText(data)}
      <ListBase
        className={classes.root}
        style={{
          backgroundColor: '#fff',
          borderRadius: 4,
          marginBottom: 8,
          marginTop: 8,
          color: '#444',
          fontSize: 14
        }}
      >
        {(greens || []).map(key => {
          return (
            <IndicatorItem
              key={key}
              field={key}
              data={data}
              color={status.green}
            />
          )
        })}

        {(yellows || []).map(key => {
          return (
            <IndicatorItem
              key={key}
              field={key}
              data={data}
              color={status.yellow}
            />
          )
        })}

        {(reds || []).map(key => {
          return (
            <IndicatorItem
              key={key}
              field={key}
              data={data}
              color={status.red}
            />
          )
        })}
      </ListBase>
    </div>
  )

  // For touch devices, use controlled mode with click handler
  // For non-touch devices, use uncontrolled mode with hover
  const tooltipProps = touchDevice
    ? {
        disableFocusListener: true,
        disableHoverListener: true,
        disableTouchListener: true, // We handle touch via click
        open: tooltipOpen,
        onClose: handleTooltipClose
      }
    : {
        disableFocusListener: true,
        disableHoverListener: false,
        disableTouchListener: true
      }

  return (
    <Tooltip
      {...tooltipProps}
      title={tooltipContent}
      arrow
    >
      <div 
        className={classes.indicator}
        onClick={handleIndicatorClick}
      >
        <ModalityStatusIndicator
          inactive={data.timeStatusColor === 'red'}
          data={data}
        />
      </div>
    </Tooltip>
  )
}

export default HealthIndicator
